import { useCallback, useMemo, useState } from 'react'

import { EMPTY_ARROW_FUNC } from '@src/config/constants'
import { CTA_TYPE, LOW_INVENTORY_THRESHOLD } from '@src/quince-components/src/atoms/productCTA/productCTAConstants'
import Product from '@src/quince-site-common/src/domains/Product'
import { useProductFormWrapper } from '@src/quince-site-pdp/src/molecules/productFormWrapper/hooks/useProductFormWrapper'
import { TPDPSectionData } from '@src/quince-site-pdp/src/types/pdp-section-data.type'

import { useProductPurchase } from '../../productPurchase/hooks/useProductPurchase'

export const useMiniPDPProductPurchase = ({
  product,
  sectionData,
}: {
  product: Product
  sectionData: TPDPSectionData
}) => {
  const [isExpandedSize, setExpandedSize] = useState(false)

  const expandSizeMiniPDP = useCallback(() => {
    setExpandedSize(true)
  }, [])

  const {
    selectedOptions,
    setSelectedOptionsState,
    productInfoRef,
    colorVariantRef,
    sizeVariantRef,
    addToCartTrackingRef,
    productProperties,
    selectedSiblingProductId,
  } = useProductPurchase({ product, recommendedContentfulProductIds: [], sectionData })

  const aboveFoldTrackingRefs = useMemo(
    () => ({ productInfoRef, colorVariantRef, sizeVariantRef, addToCartTrackingRef }),
    [productInfoRef, colorVariantRef, sizeVariantRef, addToCartTrackingRef],
  )

  const {
    errorMessage: productFormErrorMessage,
    showErrorMessage,
    inputOptionError,
    productOptions,
    onOptionSelect,
    selectedVariantPurchaseInfo,
    selectedVariant,
    purchaseOption,
    setPurchaseOption,
    selectedPlanId,
    setSelectedPlanId,
    showPurchaseTypeSelector: showPurchaseTypeSelectorRef,
    onJoinTheWaitList,
    onAddToCart,
    productCTAType,
    areAllVariantOOS,
    sizeOptionRef,
    productPurchaseInfo,
    isB2BSelfService,
    isAddToCartInProgress,
    showProductSubscriptionDeliveryMessage,
    variantSubscriptionPlans,
    formattedSubscriptionPrice,
    formattedSalePrice,
    formattedTraditionalPrice,
    formattedFestivalOfferPrice,
    savingPercentage,
    festivalPricePerUnit,
    subscriptionOfferPricePerUnit,
    subscriptionFestivalSavingPercentage,
    isDOBModalOpen,
    dateOfBirth,
    setDateOfBirth,
    dobErrorMessage,
    setDobErrorMessage,
    disableAgeVerification,
    handleDOBModalSubmit,
    handleDOBModalClose,
    isReturnable,
    brandName,
    brandLink,
    disableAddToCart,
    isCurrentProductOptionLeavingSoon,
    onClickedShopSimilar,
  } = useProductFormWrapper({
    product,
    selectedOptions,
    onSelectedOptionsChange: setSelectedOptionsState,
    aboveFoldTrackingRefs,
    onReviewSummaryClick: EMPTY_ARROW_FUNC,
    selectedSiblingProductId,
    expandSizeMiniPDP,
    addToCartTracker: 'mini_pdp_page',
  })

  const selectedVariantAvailableInventory = selectedVariantPurchaseInfo?.availableInventory ?? 0
  const showVariantAvailableInventory =
    ![CTA_TYPE.WAITLIST, CTA_TYPE.PREORDER].includes(productCTAType) &&
    selectedVariantAvailableInventory > 0 &&
    selectedVariantAvailableInventory < LOW_INVENTORY_THRESHOLD

  return {
    productProperties,
    productOptions,
    onOptionSelect,
    selectedOptions,
    productPurchaseInfo,
    inputOptionError,
    isB2BSelfService,
    aboveFoldTrackingRefs,
    selectedSiblingProductId,
    sizeOptionRef,
    selectedVariant,
    selectedVariantPurchaseInfo,
    onAddToCart,
    onJoinTheWaitList,
    productCTAType,
    areAllVariantOOS,
    productFormErrorMessage,
    showErrorMessage,
    purchaseOption,
    setPurchaseOption,
    selectedPlanId,
    setSelectedPlanId,
    showPurchaseTypeSelector: showPurchaseTypeSelectorRef.current,
    isAddToCartInProgress,
    showProductSubscriptionDeliveryMessage,
    variantSubscriptionPlans,
    formattedSubscriptionPrice,
    formattedSalePrice,
    formattedTraditionalPrice,
    formattedFestivalOfferPrice,
    savingPercentage,
    festivalPricePerUnit,
    subscriptionOfferPricePerUnit,
    subscriptionFestivalSavingPercentage,
    selectedVariantAvailableInventory,
    showVariantAvailableInventory,
    isExpandedSize,
    expandSizeMiniPDP,
    isDOBModalOpen,
    dateOfBirth,
    setDateOfBirth,
    dobErrorMessage,
    setDobErrorMessage,
    disableAgeVerification,
    handleDOBModalSubmit,
    handleDOBModalClose,
    isReturnable,
    brandName,
    brandLink,
    disableAddToCart,
    isCurrentProductOptionLeavingSoon,
    onClickedShopSimilar,
  }
}
